import React, { useEffect, useRef } from "react";

import { Tooltip } from "@mui/material";
import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  Icons,
  RowActionsExtra,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import BadgeStatus from "../../../../../../../components/Generic/BadgeStatus";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { LoadingSpinner } from "../../../../../../../components/LoadingModal/styles";
import { useAppState } from "../../../../../../../context/StateContext";
import { Carrier } from "../../../../../../../models/carrier";
import { TrackableBy } from "../../../../../../../models/trackable.by";
import { Status } from "../../../../../../../util/enums";
import CarrierModal, { CarrierModalHandle } from "./components/CarrierModal";
import DeleteModal, { DeleteModalHandler } from "./components/DeleteModal";
import { useActions } from "./hooks/useActions";
import useStyles from "./styled";
import { isShowAction } from "Util/permissions";

type Props = {
  permission?: {
    edit: boolean,
    create: boolean,
  }
}

export default function CarrierPage({ permission }: Props) {
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();
  const {
    carriers,
    totalCarrier,
    page,
    pageSize,
    isFetching,
    setPageSize,
    setCurrentPage,
    handleFilter,
    handleSearch,
    handleSort,
    create,
    update,
    getAllCarrier,
    countTotalCarrier,
  } = useActions();
  const modalRef = useRef<CarrierModalHandle>(null);
  const deleteModalRef = useRef<DeleteModalHandler>(null);
  const classes = useStyles();

  const { mutate: createCarrier, isLoading: isCreating, isSuccess: isCreatingSuccess } = create();
  const {
    mutate: updateCarrier,
    isLoading: isUpdating,
    isSuccess: isUpdatingSuccess,
    variables,
  } = update();

  const onChangeStatus = (status: string) => {
    if (status === Status.ACTIVE) return Status.INACTIVE;

    return Status.ACTIVE;
  };

  const renderChangeStatusButton = (_index: number, carrier: Carrier) => {
    if (!carrier.isDefault) {
      if (isUpdating && variables?.id === carrier.id) {
        return <LoadingSpinner size={20} />;
      }

      return (
        <Tooltip title={translate("changeStatus")} placement="top">
          <div>
            <Icons.status
              className={classes.icon}
              onClick={() => {
                if (carrier) {
                  updateCarrier({
                    id: Number(carrier.id),
                    carrier: { ...carrier, status: onChangeStatus(carrier.status) },
                  });
                }
              }}
            />
          </div>
        </Tooltip>
      );
    }
  };

  const renderEditButton = (_index: number, carrier: Carrier) => {
    if (!carrier.isDefault) {
      return (
        <Tooltip title={translate("edit")} placement="top">
          <div>
            <Icons.edit
              className={classes.icon}
              size={18}
              color="#475467"
              onClick={() => modalRef.current?.open(carrier)}
            />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const renderDeleteButton = (_index: number, carrier: Carrier) => {
    if (!carrier.isDefault) {
      return (
        <Tooltip title={translate("delete")} placement="top">
          <div>
            <Icons.delete
              className={classes.icon}
              size={18}
              color="#475467"
              onClick={() => deleteModalRef?.current?.open(carrier)}
            />
          </div>
        </Tooltip>
      );
    }
  };

  const columns: Column[] = [
    {
      name: "scac",
      title: translate("scacCode"),
    },
    {
      name: "name",
      title: translate("carrierName"),
    },
    {
      name: "abreviation",
      title: translate("carrierNameAbbreviation"),
    },
    {
      name: "trackableBy",
      title: translate("trackableBy"),
      formatText: (trackableBy: TrackableBy[]) => {
        return trackableBy.map((item) => `${item.name} - ${item.code}`).join(", ");
      },
      hideSearch: true,
      hideSort: true,
    },
    {
      name: "type",
      title: translate("type"),
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (status: string) => <BadgeStatus value={status} />,
      hideSearch: true,
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleNewRecord"],
    },
    handleNewRecord: permission && isShowAction(currentUser, permission.create)
      ? {
        text: translate("create"),
        action: () => modalRef.current?.open(),
        tooltip: translate("create"),
      }
      : undefined,
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("status"),
          column: "status",
          options: [Status.ACTIVE, Status.INACTIVE],
        },
        { name: translate("name"), column: "name" },
      ],
      onChange: handleFilter,
    },
  };

  const handleRowActionsExtraByPermission = () => {
    let actions: RowActionsExtra[] = []

    if (permission && isShowAction(currentUser, permission.edit)) {
      actions?.push({
        title: translate("edit"),
        key: "edit",
        buttonAndAction: renderEditButton,
      })
    }

    actions?.push({
      title: translate("delete"),
      key: "delete",
      buttonAndAction: renderDeleteButton,
    },
    {
      title: translate("changeStatus"),
      key: "changeStatus",
      buttonAndAction: renderChangeStatusButton,
    })

    return actions;
  }

  const rowActionsExtra: RowActionsExtra[] = handleRowActionsExtraByPermission();

  useEffect(() => {
    if (isCreatingSuccess || isUpdatingSuccess) {
      NotificationToast.success(translate("operationPerformedSuccessfully"));
      modalRef?.current?.close();
      deleteModalRef?.current?.close();
      getAllCarrier();
      countTotalCarrier();
    }
  }, [isCreatingSuccess, isUpdatingSuccess]);

  return (
    <div>
      <ShipeeziNewGrid
        data={carriers ?? []}
        columns={columns}
        totalItems={totalCarrier ?? 0}
        loadingData={isFetching}
        emptyData={translate("noInformationToBeShwon")}
        pageSize={pageSize}
        currentPage={page}
        pageSizeList={[15, 40, 60, 100]}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={generalActions}
        rowActions={{
          orderBy: ["edit", "changeStatus", "delete"],
        }}
        rowActionsExtra={rowActionsExtra}
      />

      <CarrierModal
        ref={modalRef}
        isLoading={isCreating || isUpdating}
        onSubmit={(carrier) => {
          if (carrier) {
            if (carrier.id) updateCarrier({ id: Number(carrier.id), carrier });
            else createCarrier({ ...carrier, companyId: currentUser?.companyId, isDefault: false, isAvailable: true });
          }
        }}
      />

      <DeleteModal
        ref={deleteModalRef}
        isLoading={isUpdating}
        onDelete={(carrier) => {
          if (carrier) {
            updateCarrier({ id: Number(carrier.id), carrier: { ...carrier, valid: false } });
          }
        }}
      />
    </div>
  );
}
